import React, { useEffect } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { motion } from 'framer-motion';
import { Check, Calendar, Target, Award } from 'lucide-react';
export function PrivateClassesPage() {
  useEffect(() => {
    document.title = "Private Lessons | Walker's Martial Arts | Bloomington, Illinois";
  }, []);
  return <div className="bg-black min-h-screen" data-id="element-821">
      <Navigation data-id="element-822" />

      {/* Hero Section */}
      <div className="relative pt-32 pb-20 overflow-hidden" data-id="element-823">
        <div className="absolute inset-0 z-0" data-id="element-824">
          <img src="https://images.unsplash.com/photo-1599058945522-28d584b6f0ff?q=80&w=2069&auto=format&fit=crop" alt="Private Lessons" className="w-full h-full object-cover opacity-20" data-id="element-825" />
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/90 to-black" data-id="element-826" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-827">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-828">
            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6" data-id="element-829">
              <span className="text-[#00D4FF]" data-id="element-830">Private Lessons</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl leading-relaxed" data-id="element-831">
              One-on-one instruction tailored to your specific goals and
              schedule. Accelerate your progress with personalized attention.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Benefits */}
      <section className="py-20 bg-[#0a0a0a]" data-id="element-832">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-833">
          <h2 className="text-3xl font-bold text-white mb-12 text-center" data-id="element-834">
            Why Choose Private Lessons?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-835">
            {[{
            icon: Target,
            title: 'Personalized Training',
            description: 'Curriculum designed specifically for your goals, skill level, and learning pace.'
          }, {
            icon: Calendar,
            title: 'Flexible Scheduling',
            description: 'Train on your schedule with convenient appointment times that work for you.'
          }, {
            icon: Award,
            title: 'Accelerated Progress',
            description: 'Learn faster with focused attention and immediate feedback from expert instructors.'
          }].map((benefit, index) => <motion.div key={benefit.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-[#1a1a1a] p-8 rounded-2xl border border-gray-800 text-center" data-id="element-836">
                <div className="w-16 h-16 bg-[#00D4FF]/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-837">
                  <benefit.icon className="w-8 h-8 text-[#00D4FF]" data-id="element-838" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4" data-id="element-839">
                  {benefit.title}
                </h3>
                <p className="text-gray-400" data-id="element-840">{benefit.description}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Details */}
      <section className="py-20 bg-black" data-id="element-841">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-842">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-843">
            <div data-id="element-844">
              <h2 className="text-3xl font-bold text-white mb-6" data-id="element-845">
                Perfect For
              </h2>
              <div className="space-y-4" data-id="element-846">
                {['Students preparing for belt tests or competitions', 'Beginners who want extra attention to build confidence', 'Advanced students refining specific techniques', 'Busy professionals needing flexible scheduling', 'Anyone wanting to accelerate their martial arts journey', 'Families training together'].map(item => <div key={item} className="flex items-start gap-3" data-id="element-847">
                    <Check className="w-5 h-5 text-[#00D4FF] shrink-0 mt-1" data-id="element-848" />
                    <p className="text-gray-300" data-id="element-849">{item}</p>
                  </div>)}
              </div>
            </div>
            <div className="bg-[#1a1a1a] p-8 rounded-2xl border border-gray-800" data-id="element-850">
              <h3 className="text-2xl font-bold text-white mb-6" data-id="element-851">
                Available Times
              </h3>
              <div className="space-y-4 text-gray-300" data-id="element-852">
                <div data-id="element-853">
                  <p className="font-semibold text-[#00D4FF]" data-id="element-854">
                    Monday - Thursday
                  </p>
                  <p data-id="element-855">5:00 AM - 9:00 PM</p>
                </div>
                <div data-id="element-856">
                  <p className="font-semibold text-[#00D4FF]" data-id="element-857">Friday</p>
                  <p data-id="element-858">5:00 AM - 7:00 PM</p>
                </div>
                <div data-id="element-859">
                  <p className="font-semibold text-[#00D4FF]" data-id="element-860">Saturday</p>
                  <p data-id="element-861">9:00 AM - 2:00 PM</p>
                </div>
                <div data-id="element-862">
                  <p className="font-semibold text-[#00D4FF]" data-id="element-863">Sunday</p>
                  <p data-id="element-864">8:00 AM - 12:00 PM</p>
                </div>
              </div>
              <div className="mt-8 pt-8 border-t border-gray-800" data-id="element-865">
                <p className="text-gray-400 text-sm" data-id="element-866">
                  Contact us to schedule your private lesson and discuss your
                  specific goals.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0a0a0a]" data-id="element-867">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-868">
          <h2 className="text-3xl font-bold text-white mb-6" data-id="element-869">
            Ready to Schedule Your Private Lesson?
          </h2>
          <p className="text-xl text-gray-300 mb-8" data-id="element-870">
            Contact us to discuss your goals and schedule your personalized
            training session.
          </p>
          <a href="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-[#00D4FF] text-black font-bold rounded-full hover:bg-[#33ddff] transition-colors shadow-[0_0_20px_rgba(0,212,255,0.4)]" data-id="element-871">
            Contact Us for Details
          </a>
        </div>
      </section>

      <Footer data-id="element-872" />
    </div>;
}